import type { DefaultNamespace, ParseKeys, TOptions } from '@if/core/i18n'
import { EPaymentPlan, EProvince } from '@if/core/types'
import { FundingComputedDto } from '@src/data/types/FundingComputedDto'
import { MerchantPaymentPlan } from '@src/data/types/MerchantPaymentPlan'
import { ProvinceConfigs } from '@src/data/types/ProvinceConfigs'
import { createContext, useContext, useEffect } from 'react'
import { BaseWorksheetFormConfig } from './BaseWorksheetForm.config'
import { getMaxPmtAmountWithAgentLeeway } from './BaseWorksheetForm.helpers'

// Matches i18next's `t(key, options)` shape so it's directly assignable at call sites.
export type WorksheetErrorMessageArgs = Omit<TOptions, 'context'> & { context?: string }

// messageKey is compile-time-validated against the i18n key union.
export interface WorksheetError {
  code: string
  messageKey: ParseKeys<DefaultNamespace>
  messageArgs?: WorksheetErrorMessageArgs
}

export type ErrorRegistry = (id: string, error: WorksheetError | null) => void

const ErrorRegistryContext = createContext<ErrorRegistry | null>(null)
export const ErrorRegistryProvider = ErrorRegistryContext.Provider

export const useReportError = (id: string, error: WorksheetError | null) => {
  const register = useContext(ErrorRegistryContext)
  if (!register) {
    throw new Error('useReportError must be used inside <BaseWorksheetForm onError={...}>')
  }
  useEffect(() => {
    register(id, error)
    return () => register(id, null)
  }, [register, id, error])
}

const shallowEqualArgs = (
  a: WorksheetErrorMessageArgs | undefined,
  b: WorksheetErrorMessageArgs | undefined,
): boolean => {
  if (a === b) return true
  if (!a || !b) return !a && !b
  const aRec = a as Record<string, unknown>
  const bRec = b as Record<string, unknown>
  const keys = Object.keys(aRec)
  if (keys.length !== Object.keys(bRec).length) return false
  return keys.every((k) => aRec[k] === bRec[k])
}

export const areWorksheetErrorsEqual = (a: WorksheetError, b: WorksheetError): boolean =>
  a.code === b.code && a.messageKey === b.messageKey && shallowEqualArgs(a.messageArgs, b.messageArgs)

interface AprValidatorInput {
  computedInfo: FundingComputedDto | null
  stateIso: EProvince | undefined
  effectiveRateThreshold: number
  provinceConfigs: ProvinceConfigs
}

// Binding threshold: lower of program legal cap and province high-cost rate (NL only).
export const validateApr = ({
  computedInfo,
  stateIso,
  effectiveRateThreshold,
  provinceConfigs,
}: AprValidatorInput): WorksheetError | null => {
  if (!computedInfo) return null
  const effectiveRate = computedInfo.effectiveRate ?? 0
  const candidates: number[] = []
  if (effectiveRateThreshold > 0) candidates.push(effectiveRateThreshold)
  if (stateIso === EProvince.Newfoundland) {
    const provinceRate = provinceConfigs[stateIso]?.highCostRate
    if (provinceRate && provinceRate > 0) candidates.push(provinceRate)
  }
  const threshold = candidates.length > 0 ? Math.min(...candidates) : 0
  if (threshold <= 0 || effectiveRate < threshold) return null
  return {
    code: 'aprTooHigh',
    messageKey: 'worksheetCommon.errors.aprTooHigh',
    messageArgs: { threshold, effectiveRate, stateIso },
  }
}

interface PaymentTooHighInput {
  computedInfo: FundingComputedDto | null
  maxPmtAmount: number
  // Matches the leeway-adjusted ceiling used by the continuous Term-field check.
  useAgentLeeway?: boolean
  paymentPlanId?: string
}

export const validatePaymentTooHigh = ({
  computedInfo,
  maxPmtAmount,
  useAgentLeeway,
  paymentPlanId,
}: PaymentTooHighInput): WorksheetError | null => {
  if (!computedInfo || maxPmtAmount <= 0) return null
  const effectiveMax = useAgentLeeway ? getMaxPmtAmountWithAgentLeeway(paymentPlanId, maxPmtAmount) : maxPmtAmount
  const payment = computedInfo.paymentForFrequency ?? 0
  if (payment <= effectiveMax) return null
  return {
    code: 'pmtTooHigh',
    messageKey: 'worksheetCommon.errors.pmtTooHigh',
    messageArgs: { maxPayment: effectiveMax, payment },
  }
}

interface AmountUnderCreditInput {
  amountRequested: number
  availableCredit: number
}

export const validateAmountUnderCredit = ({
  amountRequested,
  availableCredit,
}: AmountUnderCreditInput): WorksheetError | null => {
  if (amountRequested <= availableCredit) return null
  return {
    code: 'amountOverCredit',
    messageKey: 'worksheetCommon.errors.amountOverCredit',
    messageArgs: { availableCredit, amountRequested },
  }
}

interface ActivationWithin90DaysInput {
  activationDate: Date | string
  hardHitReportedOn: Date | string | null | undefined
}

const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000

export const validateActivationWithin90Days = ({
  activationDate,
  hardHitReportedOn,
}: ActivationWithin90DaysInput): WorksheetError | null => {
  if (!hardHitReportedOn) return null
  const reportTs = new Date(hardHitReportedOn).getTime()
  const activationTs = new Date(activationDate).getTime()
  if (activationTs - reportTs <= NINETY_DAYS_MS) return null
  return {
    code: 'activationAfter90Days',
    messageKey: 'worksheetCommon.errors.activationAfter90Days',
  }
}

interface PaymentPlanAvailableInput {
  paymentPlanId: string
  availablePaymentPlans: MerchantPaymentPlan[]
}

// Promo plans must appear in availablePaymentPlans; RegularDailyInterests always passes.
export const validatePaymentPlanAvailable = ({
  paymentPlanId,
  availablePaymentPlans,
}: PaymentPlanAvailableInput): WorksheetError | null => {
  if (paymentPlanId === (EPaymentPlan.RegularDailyInterests as string)) return null
  if (availablePaymentPlans.some((p) => (p.id as string) === paymentPlanId)) return null
  return {
    code: 'paymentPlanNotAvailable',
    messageKey: 'worksheetCommon.errors.paymentPlanNotAvailable',
  }
}

export interface SubmitValidatorContext {
  signal: AbortSignal
}

export type SyncSubmitValidator = () => WorksheetError | null
export type AsyncSubmitValidator = (ctx: SubmitValidatorContext) => Promise<WorksheetError | null>
export type SubmitValidator = SyncSubmitValidator | AsyncSubmitValidator

export type SubmitValidationResult = { ok: true } | { ok: false; errors: WorksheetError[] }

const isAsyncSubmitValidator = (v: SubmitValidator): v is AsyncSubmitValidator => v.length > 0

// Sync validators are collect-all so the user fixes everything at once.
// Async validators are fail-fast to avoid extra server round-trips.
// Aborted runs throw an AbortError that callers should swallow.
export const runSubmitValidations = async (
  validators: readonly SubmitValidator[],
  signal: AbortSignal,
): Promise<SubmitValidationResult> => {
  const syncErrors: WorksheetError[] = []
  const asyncValidators: AsyncSubmitValidator[] = []

  for (const v of validators) {
    if (isAsyncSubmitValidator(v)) {
      asyncValidators.push(v)
      continue
    }
    const result = v()
    if (result) syncErrors.push(result)
  }
  if (syncErrors.length > 0) return { ok: false, errors: syncErrors }

  for (const v of asyncValidators) {
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError')
    const result = await v({ signal })
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError')
    if (result) return { ok: false, errors: [result] }
  }
  return { ok: true }
}

export interface DefaultSubmitValidatorInputs {
  config: BaseWorksheetFormConfig
  computedInfo: FundingComputedDto | null
  provinceConfigs: ProvinceConfigs
  // Each input is gated by the matching config flag; missing inputs skip silently.
  paymentPlanId?: string
  amountRequested?: number
  availableCredit?: number
  activationDate?: Date | string | null
  hardHitReportedOn?: Date | string | null
}

// Standard config-driven validators. Program-specific checks compose by
// spreading the result and appending.
export const buildDefaultSubmitValidators = ({
  config,
  computedInfo,
  provinceConfigs,
  paymentPlanId,
  amountRequested,
  availableCredit,
  activationDate,
  hardHitReportedOn,
}: DefaultSubmitValidatorInputs): SubmitValidator[] => {
  const validators: SubmitValidator[] = []
  if (config.effectiveRateThreshold > 0) {
    validators.push(() =>
      validateApr({
        computedInfo,
        stateIso: config.stateIso,
        effectiveRateThreshold: config.effectiveRateThreshold,
        provinceConfigs,
      }),
    )
  }
  if (config.validateMaxPayment && config.maxPmtAmount > 0) {
    validators.push(() =>
      validatePaymentTooHigh({
        computedInfo,
        maxPmtAmount: config.maxPmtAmount,
        useAgentLeeway: config.useAgentLeeway,
        paymentPlanId,
      }),
    )
  }
  if (config.checkAvailableCredit && amountRequested != null && availableCredit != null) {
    validators.push(() => validateAmountUnderCredit({ amountRequested, availableCredit }))
  }
  if (config.checkActivationWithin90Days && activationDate != null) {
    validators.push(() => validateActivationWithin90Days({ activationDate, hardHitReportedOn }))
  }
  if (paymentPlanId) {
    validators.push(() =>
      validatePaymentPlanAvailable({
        paymentPlanId,
        availablePaymentPlans: config.availablePaymentPlans,
      }),
    )
  }
  return validators
}
